<template>
  <li class="catalog__tab-item">
    <div ref="filterContainer">
      <div class="catalog__tab-item-head">
        <a :class="{'collapsed': !shown}" role="button" @click="toggle">
          <h3>
            {{ title }}
            <img v-if="tooltip" v-tippy="{ arrow : true }"
              src="/img/ic_info_line.svg" alt=""
              :title="tooltip">
              <span v-if="hint" class="hint-text">{{ hint }}</span>
          </h3>
          <img class="catalog__tab-item-head-caretka" src="/img/ic_arrow-sm.svg" alt="">
        </a>
      </div>
      <div class="collapse" :class="{'show': shown}">
        <div class="well">
          <ul class="catalog__catalog-list">
            <li class="catalog__catalog-item">
              <form class="catalog__catalog-item-form">
                <input class="input-conditions" type="text" placeholder="Поиск" v-model="search">
                <reset-button v-if="search !== ''"  @click="search = ''" class="catalog__input-btn-reset" />
              </form>
            </li>
            <template v-if="loading">
              <content-placeholders>
                <content-placeholders-text :lines="7" />
              </content-placeholders>
            </template>
            <template v-else>
              <li v-if="!limitItems.length">Ничего не найдено</li>
              <li v-for="[id, item] in limitItems" :key="`block-${filter}-${id}`" class="catalog__catalog-item">
                <filter-checkbox
                  :disabled="!item.active"
                  :label="item.title"
                  :hint="item.short_description"
                  :value="id"
                  :limit="limit"
                  :filter="filter"
                  @click.native.prevent="item.active ? setFilter(id, true) : null" />
              </li>
            </template>
          </ul>
        </div>
      </div>
      <div v-if="filter.length || shown" class="catalog__catalog-item-btns">
        <a v-if="filter.length" @click="setFilter(null, true)" href="javascript:void(0);" class="btn linkBlueConditions">Очистить <span>({{ filter.length }})</span></a>
        <a
          v-if="entriesItems.length > 5"
          @click="setShowAll(true)"
          href="javascript:void(0);"
          class="btn btn-link btn-default linkBlueConditions"
          :class="filter.length ? 'btn-bold' : ''">
          <template v-if="!filter.length">Показать все <span>({{ entriesItems.length }})</span></template>
          <template v-else>Выбрано <span>({{ filter.length }})</span></template>
        </a>
      </div>

      <div v-if="showAll" class="popaps-item-wrap">
        <div class="popaps-item" :class="entriesItems.length <= 15 ? 'shortList' : 'longList'">
          <div class="popap__content">
            <div class="popap__content-header">
              <h3 class="popap__content-title">
                {{ title }} ({{ entriesItems.length }})
              </h3>
              <div @click="setShowAll(false)" class="popap__btn-close">
                <i><img src="/img/ic_close_chip.svg" alt=""></i>
              </div>
            </div>
            <filter-block-popup-short
              v-if="entriesItems.length <= 15"
              :items="sortedItems"
              :filter="filter"
              :limit="limit"
              :isCategorized="isCategorized"
              @filter="setFilter($event)" />
            <filter-block-popup-long
              v-else
              :items="filteredItems"
              :filter="filter"
              :limit="limit"
              :search="search"
              :isCategorized="isCategorized"
              @search="search = $event"
              @filter="setFilter($event)" />
            <button @click="applyShowAll" class="btn popap__content-btn" type="button">
              Применить
            </button>
          </div>
        </div>
      </div>
    </div>
  </li>
</template>

<script>
import FilterBlockPopupShort from '@/components/FilterBlockPopupShort'
import FilterBlockPopupLong from '@/components/FilterBlockPopupLong'
import FilterCheckbox from '@/components/FilterCheckbox'
import { SET_FILTER, SEARCH } from '@/store/actions/search'
import { SET_FOUND_DOM } from '@/store/actions/found'
import { mapGetters } from 'vuex'
import has from 'lodash/has'
import orderBy from 'lodash/orderBy'

const LIMIT_ITEMS = 5
const EVENT_FILTER_SHOW_ALL = 'app::filter-block::show-all'
export const EVENT_FILTER_SHOWN_TOGGLE = 'app::filter-block::shown-toggle'

export default {
  name: 'filter-block',
  props: {
    title: {
      type: String,
      require: true
    },
    hint: {
      type: String,
      require: false
    },
    tooltip: {
      type: String
    },
    type: {
      type: String,
      require: true
    },
    filterKey: {
      type: String,
      require: true
    },
    forceShown: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    isCategorized: {
      type: Boolean,
      default: false
    }
  },
  components: {
    FilterBlockPopupShort,
    FilterBlockPopupLong,
    FilterCheckbox
  },
  data: () => ({
    shown: false,
    showAll: false,
    search: ''
  }),
  created () {
    const shown = parseInt(localStorage.getItem(`filter-block-${this.filterKey}-shown`))
    if ((isNaN(shown) && this.forceShown) || (!isNaN(shown) && shown)) {
      this.shown = true
    }
    this.$root.$on(EVENT_FILTER_SHOW_ALL, this.hideAll)
  },
  computed: {
    items () {
      return this.getData(this.type, this.filterKey)
    },
    filter () {
      return this.getFilter(this.type, this.filterKey)
    },
    limit () {
      return this.getLimitation(this.type, this.filterKey)
    },
    entriesItems () {
      return Object.entries(this.items).reduce((allItems, [key, item]) => {
        let keyIsValid = true
        if (this.type !== 'static') {
          key = parseInt(key)
          keyIsValid = !isNaN(key)
        }
        if (keyIsValid) {
          allItems.push([key, { ...item,
            active: item.active && (this.limit === false || this.limit.includes(key)),
            checked: this.filter.indexOf(key) !== -1
          }])
        }

        return allItems
      }, [])
    },
    filteredItems () {
      const items = [ ...this.sortedItems ]
      return this.search !== ''
        ? items.filter(([, item]) => {
          const description = item.description ? item.description : item.short_description
          return item.title.toLowerCase().includes(this.search.toLowerCase()) ||
          (description && description.toLowerCase().includes(this.search.toLowerCase()))
        }) : items
    },
    sortedItems () {
      const items = [ ...this.entriesItems ]
      return orderBy(items, [([, o]) => o.active, ([, o]) => o.checked, ([, o]) => o.title], ['desc', 'desc', 'asc'])
    },
    limitItems () {
      const items = [ ...this.filteredItems ]
      return items.splice(0, LIMIT_ITEMS)
    },
    ...mapGetters(['getData', 'getFilter', 'getLimitation', 'getFoundDom', 'getType'])
  },
  methods: {
    toggle () {
      this.shown = !this.shown
      localStorage.setItem(`filter-block-${this.filterKey}-shown`, +this.shown)
      if (!this.shown && this.getFoundDom === this.$refs.filterContainer) {
        this.$store.dispatch(SET_FOUND_DOM, null)
      }
      this.$root.$emit(EVENT_FILTER_SHOWN_TOGGLE)
    },
    setShowAll (value) {
      this.showAll = value
      this.$root.$emit(EVENT_FILTER_SHOW_ALL, this.$el)
      if (!value) {
        this.$store.dispatch(SET_FOUND_DOM, null)
      }
    },
    hideAll ($el) {
      if (this.$el !== $el) {
        this.showAll = false
      }
    },
    setFilter (value = null, dom = false) {
      const foundFilter = !this.filter.includes(value)
      if (value !== null && foundFilter && this.limit !== false && !this.limit.includes(value)) {
        return
      }
      if (value !== null && foundFilter && has(this.items, [value, 'title']) && this.$gtm) {
        this.$gtm.trackEvent({
          event: 'UA event',
          category: 'Фильтр',
          action: this.getType === 'common' ? 'Общее' : 'Тарифное',
          label: this.items[value].title
        })
      }

      const { type, filterKey: filter } = this
      this.$store.dispatch(SET_FILTER, { type, filter, value }).then(() => {
        this.$store.dispatch(SET_FOUND_DOM, { dom: dom ? this.$refs.filterContainer : null })
      })
    },
    applyShowAll () {
      this.setShowAll(false)
      const { $router: router } = this
      this.$store.dispatch(SEARCH, { router })
    }
  },
  beforeDestroy () {
    this.$root.$off(EVENT_FILTER_SHOW_ALL, this.hideAll)
  }
}
</script>